import { Stack } from '@/components/layout';
import { PlayerFilterSheet } from '@/features/player/components/player-filter-sheet';
import { PlayerSortSheet } from '@/features/player/components/player-sort-sheet';
import { useFilterPlayer } from '@/features/player/hooks/use-filter-player';
import { useScaling } from '@/hooks';
import React, { memo, useEffect } from 'react';

import { usePlayerListTemplate } from './context';
import { markSlot } from './slot-marker';

export type PlayerListTemplateV2FilterProps = {
  /** Hide the filter sheet trigger. Default: false. */
  hideFilter?: boolean;
  /** Hide the sort sheet trigger. Default: false. */
  hideSort?: boolean;
};

/**
 * Renders the filter + sort triggers (and their bottom sheets) in a row.
 * State is persisted across screens via the existing Player filter/sort
 * providers (hoisted by `PlayerListTemplateV2.Providers`).
 */
function FilterComponent({ hideFilter, hideSort }: PlayerListTemplateV2FilterProps) {
  const { scale, isTablet } = useScaling();
  const { listLogic } = usePlayerListTemplate();

  const {
    open,
    setOpen,
    level,
    draftLevel,
    setDraftLevel,
    draftLastActive,
    setDraftLastActive,
    filterButtonLabel,
    closeWithoutSave,
    saveAndClose,
    LEVELS,
    lastActiveOptions,
    sortOpen,
    setSortOpen,
    sortKey,
    sortOrder,
    sortDraft,
    setSortDraft,
    sortButtonLabel,
    closeSortWithoutSave,
    saveSortAndClose,
    forceSortToNameAsc,
    lastActive,
  } = useFilterPlayer();

  useEffect(() => {
    if (!open) return;
    setDraftLevel(level);
    setDraftLastActive(lastActive);
  }, [lastActive, level, open, setDraftLastActive, setDraftLevel]);

  useEffect(() => {
    if (level == null) return;
    if (sortKey === 'level') {
      forceSortToNameAsc();
      return;
    }
    if (sortDraft.key === 'level') {
      setSortDraft({ key: 'name', order: 'asc' });
    }
  }, [forceSortToNameAsc, level, setSortDraft, sortDraft.key, sortKey]);

  return (
    <Stack direction="row" gap={isTablet ? scale(12) : scale(4)}>
      {hideFilter ? null : (
        <PlayerFilterSheet
          open={open}
          onOpenChange={setOpen}
          filterButtonLabel={filterButtonLabel}
          closeWithoutSave={closeWithoutSave}
          saveAndClose={() => {
            listLogic.onChangePage?.(1);
            saveAndClose();
          }}
          levels={LEVELS}
          draftLevel={draftLevel}
          onDraftLevelChange={setDraftLevel}
          lastActiveOptions={lastActiveOptions}
          draftLastActive={draftLastActive}
          onDraftLastActiveChange={setDraftLastActive}
        />
      )}

      {hideSort ? null : (
        <PlayerSortSheet
          open={sortOpen}
          onOpenChange={setSortOpen}
          sortButtonLabel={sortButtonLabel}
          closeWithoutSave={closeSortWithoutSave}
          saveAndClose={() => {
            listLogic.onChangePage?.(1);
            saveSortAndClose();
          }}
          levelFilter={level}
          saved={{ key: sortKey, order: sortOrder }}
          draft={sortDraft}
          setDraft={setSortDraft}
        />
      )}
    </Stack>
  );
}

const MemoFilter = memo(FilterComponent);
MemoFilter.displayName = 'PlayerListTemplateV2.Filter';

export const Filter = markSlot(MemoFilter, 'filter');
